import React, { useEffect, useState } from "react";
import { createSelector } from "@reduxjs/toolkit";
import BreadCrumb from "Common/BreadCrumb";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileEdit,
  Loader2,
  Search,
  Trash2,
} from "lucide-react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  deleteBlog as onDeleteBlog,
  getBlogs as onGetBlogs,
} from "slices/thunk";
import { Link } from "react-router-dom";
import { Dropdown } from "Common/Components/Dropdown";
import DeleteModal from "Common/DeleteModal";

const Blogs = () => {
  const dispatch = useDispatch<any>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<any>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const blogStatus = ["all", "published", "draft", "archived"];
  const [selectedBlogId, setSelectedBlogId] = useState<string>("");

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      blogs: state.blogs.blogs,
      isLoading: state.isLoading,
      pagination: state.pagination,
    })
  );
  const { blogs, isLoading, pagination } = useSelector(selectDataList);

  // const deleteToggle = () => setDeleteModal(!deleteModal);

  const handleDeleteBlog = () => {
    dispatch(onDeleteBlog(selectedBlogId));
    setDeleteModal(false);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
  //  const getPages = () => Array.from({ length: totalPages }, (_, i) => i + 1);

  const getPages = () => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages: (number | "...")[] = [1]; // Always show the first page

    if (currentPage > 3) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) pages.push("...");

    pages.push(totalPages); // Always show the last page

    return pages;
  };

  useEffect(() => {
    setCurrentPage(1); // Reset page to 1 when status changes
  }, [selectedStatus]);

  useEffect(() => {
    const payload = {
      page: currentPage,
      limit: pagination?.limit,
      status: selectedStatus,
    };
    dispatch(onGetBlogs(payload));
  }, [dispatch, currentPage, pagination?.limit, selectedStatus]);

  useEffect(() => {
    let filteredBlogs = blogs;
    if (selectedStatus !== "all") {
      filteredBlogs = blogs.filter(
        (blog: any) => blog.status === selectedStatus
      );
    }
    setData(filteredBlogs);
  }, [blogs, selectedStatus]);
  
  return (
    <div>
      <BreadCrumb title="Blogs" pageTitle="Blog" />
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={() => handleDeleteBlog()}
        message="Are you sure you want to remove this subsection?"
      />
      <Dropdown className="relative">
        <Dropdown.Trigger
          type="a"
          href="#!"
          className="bg-white border-white dropdown-toggle text-custom-500 btn hover:text-custom-700 focus:text-custom-700 active:text-custom-700 dark:bg-zink-700 dark:border-zink-700"
          id="dropdownMenuLink"
          data-bs-toggle="dropdown"
        >
          {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)}
          {/* Link Dropdown{" "} */}
          <ChevronDown className="inline-block size-4 ltr:ml-1 rtl:mr-1"></ChevronDown>
        </Dropdown.Trigger>

        <Dropdown.Content
          placement="right-end"
          as="ul"
          className="absolute !transform-none !inset-auto z-50 py-2 mt-1 list-none bg-white rounded-md shadow-md ltr:text-left rtl:text-right dropdown-menu min-w-max dark:bg-zink-600"
          aria-labelledby="dropdownMenuLink"
        >
          {blogStatus.map((type) => (
            <li key={type}>
              <button
                onClick={() => setSelectedStatus(type)}
                className={`block px-4 py-1.5 text-base font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            </li>
          ))}
        </Dropdown.Content>
      </Dropdown>
      <div className="pt-5">
        {isLoading ? (
          <div className="relative flex items-center justify-center min-h-full">
            <div className="flex justify-center fixed top-[50%]">
              <Loader2 className="size-[35px] ltr:mr-2 rtl:ml-2 animate-spin" />
            </div>
          </div>
        ) : (
          <div>
            {data?.map((blog: any) => {
              return (
                <div key={blog._id} className="card p-5">
                  <div className="flex justify-between">
                    <div className="flex items-center gap-x-5">
                      <div className="">
                        <img
                          className="rounded-md w-20 h-20"
                          src={blog.coverImage}
                        />
                      </div>
                      <div>
                        <p className="text-[16px] font-semibold">
                          {blog.title}
                        </p>
                        <div className="flex items-center gap-x-2">
                          <p
                            className={`${
                              blog.status === "draft"
                                ? "text-[#FFA500]"
                                : "text-[#22C55E]"
                            }`}
                          >
                            {blog.status.charAt(0).toUpperCase() +
                              blog.status.slice(1)}
                          </p>
                          <p className="text-[#818181] text-sm">
                            {new Date(blog.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                timeZone: "Asia/Kolkata",
                              }
                            )}
                          </p>
                        </div>
                        <div className="flex pt-3 gap-x-2">
                          {blog.tags
                            .slice(0, 3)
                            .map((tag: string, index: number) => (
                              <span
                                key={index}
                                className="px-2.5 py-0.5 text-xs inline-block font-medium rounded-full border bg-white border-slate-400 text-slate-500 dark:bg-zink-700 dark:border-zink-400 dark:text-zink-200"
                              >
                                {tag}
                              </span>
                            ))}
                          {/* Show "+X More" if there are more than 3 tags */}
                          {blog.tags.length > 3 && (
                            <Link
                              to={`/preview-blog/${blog._id}?scrollTo=tags`}
                            >
                              <span className="px-2.5 py-0.5 text-xs inline-block font-medium rounded-full border bg-white border-slate-400 text-slate-500 dark:bg-zink-700 dark:border-zink-400 dark:text-zink-200">
                                {/* //  <span className="px-2.5 py-0.5 text-xs inline-block font-medium rounded-full border bg-gray-100 border-gray-400 text-gray-600 dark:bg-zink-700 dark:border-zink-400 dark:text-zink-200">  */}
                                +{blog.tags.length - 3} More
                              </span>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end">
                      <Link to={`/update-blog/${blog._id}`}>
                        <button
                          type="button"
                          className="flex items-center justify-center size-[25.5px] transition-all duration-200 ease-linear p-0 text-blue-500 bg-white btn hover:text-blue-500 hover:bg-blue-100 focus:text-blue-500 focus:bg-blue-100 active:text-blue-500 active:bg-blue-100 dark:bg-zink-700 dark:hover:bg-blue-500/10 dark:focus:bg-blue-500/10 dark:active:bg-blue-500/10"
                        >
                          <FileEdit className="size-4" />
                        </button>
                      </Link>
                      <button
                        type="button"
                        className="flex items-center justify-center size-[25.5px] transition-all duration-200 ease-linear p-0 text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                        onClick={() => {
                          setDeleteModal(true);
                          setSelectedBlogId(blog._id);
                        }}
                      >
                        <Trash2 className="size-4" />
                      </button>
                      <Link to={`/preview-blog/${blog._id}`}>
                        <button
                          type="button"
                          className="flex items-center justify-center size-[25.5px] transition-all duration-200 ease-linear p-0 text-green-500 bg-white btn hover:text-green-500 hover:bg-green-100 focus:text-green-500 focus:bg-green-100 active:text-green-500 active:bg-green-100 dark:bg-zink-700 dark:hover:bg-green-500/10 dark:focus:bg-green-500/10 dark:active:bg-green-500/10"
                        >
                          <Eye className="size-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
            {!data?.length && (
              <div className="noresult">
                <div className="py-6 text-center">
                  <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                  <h5 className="mt-2 mb-1">No Blogs Available</h5>
                  <p className="mb-0 text-slate-500 dark:text-zink-200">
                    {/* There are no blogs matching your selected filter. */}
                    {`It looks like there are no blogs available under ${selectedStatus}.`}
                  </p>
                </div>
              </div>
            )}
            {totalPages >= 1 && (
              <ul className="flex justify-end pt-8 flex-wrap items-center gap-2 shrink-0">
                {/* Prev Button */}
                <li>
                  <button
                    onClick={() =>
                      currentPage > 1 && onPageChange(currentPage - 1)
                    }
                    disabled={currentPage === 1}
                    className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                      currentPage !== 1
                        ? "hover:text-blue-500"
                        : "text-gray-300"
                    }`}
                  >
                    <ChevronLeft />
                  </button>
                </li>
                {/* Page Numbers */}
                {getPages().map((page) => (
                  <li key={page}>
                    <button
                      onClick={() => onPageChange(page as number)}
                      className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 ${
                        currentPage === page
                          ? "bg-blue-500 text-blue-500"
                          : "hover:bg-gray-200"
                      }`}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                {/* Next Button */}
                <li>
                  <button
                    onClick={() =>
                      currentPage < totalPages && onPageChange(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                    className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                      currentPage !== totalPages
                        ? "hover:text-blue-500"
                        : "text-gray-300"
                    }`}
                  >
                    <ChevronRight />
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;
